import React from "react";

import { css } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod/v4-mini";

import { useStyles } from "@/hooks/useStyles.ts";
import {
  CarePlanInterruptionReason,
  carePlanInterruptionReasonSchema,
  carePlanInterruptionReasonTranslationMapping,
  carePlanInterruptionReasons,
} from "@/models/CarePlanInterruptionModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Dialog } from "@/uiKit/atoms/Dialog.tsx";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { SelectItem } from "@/uiKit/atoms/inputs/SelectItem.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { FormSelectInput } from "@/uiKit/molecules/form/FormSelectInput.tsx";

export type InterruptCarePlanModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: CarePlanInterruptionReason) => void;
  isPending?: boolean;
};

type InterruptCarePlanFormData = {
  reason: CarePlanInterruptionReason;
};

const interruptCarePlanFormSchema = z.object({
  reason: carePlanInterruptionReasonSchema,
});

export const InterruptCarePlanModal: React.FC<InterruptCarePlanModalProps> = ({
  open,
  onClose,
  onSubmit,
  isPending = false,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const formContext = useForm<InterruptCarePlanFormData>({
    resolver: zodResolver(interruptCarePlanFormSchema),
  });

  return (
    <Dialog id="interrupt-care-plan-dialog" open={open} onClose={onClose} maxWidth="sm">
      <Form formContext={formContext} onSubmit={data => onSubmit(data.reason)}>
        <CardHeader
          id="interrupt-care-plan-header"
          title={t("carePlanInterruption.modalTitle")}
          onExit={onClose}
          variant="contained"
        />
        <CardContent className={styles.content}>
          <Typography variant="body">{t("carePlanInterruption.modalSubtitle")}</Typography>
          <FormSelectInput id="interrupt-care-plan-reason" name="reason" fullWidth reserveErrorSpace>
            {carePlanInterruptionReasons.map(reason => (
              <SelectItem key={reason} value={reason}>
                {t(carePlanInterruptionReasonTranslationMapping(reason))}
              </SelectItem>
            ))}
          </FormSelectInput>
        </CardContent>
        <CardFooter className={styles.footer}>
          <Button variant="outlined" data-action="care-plan.interruption.cancel" onClick={onClose} disabled={isPending}>
            {t("button.cancel")}
          </Button>
          <Button
            id="interrupt-care-plan-submit"
            variant="contained"
            data-action="care-plan.interruption.submit"
            type="submit"
            pending={isPending}
          >
            {t("button.validate")}
          </Button>
        </CardFooter>
      </Form>
    </Dialog>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css({
    paddingTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
  }),
  footer: css({
    paddingBottom: theme.spacing(8),
    gap: theme.spacing(8),
  }),
});
